/**
 * Badge — the soft-badge (design.md §1 status table + §2/§5):
 * tinted fill + EXPLICIT border token + colored text, radius 6px.
 *
 * Soft-badges are for **category / priority / flags ONLY** ("K3", "Prio Hoch",
 * "KI erkannt") — NEVER for status. Status renders as <StatusDot /> (dot +
 * colored text, no fill).
 *
 * Escalation scales (K0–K3, priority low→high): the resting pole is Neutral,
 * then Info → Warning → Danger. Green (`success`) is reserved for genuine
 * success events — never the neutral resting state (§2).
 *
 * Variants: `danger` / `warning` / `success` / `info` are the contract names;
 * the pre-U1 names `alert` / `decide` / `ok` remain accepted aliases.
 */
import type { HTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/cn';

const dangerClasses = 'border-status-danger-border bg-status-danger-bg text-status-danger';
const warningClasses = 'border-status-warning-border bg-status-warning-bg text-status-warning';
const successClasses = 'border-status-success-border bg-status-success-bg text-status-success';
const infoClasses = 'border-status-info-border bg-status-info-bg text-status-info';

export const badgeVariants = cva(
  'inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs font-medium [font-variant-numeric:tabular-nums] [&_svg]:size-3',
  {
    variants: {
      variant: {
        default: 'border-transparent bg-primary text-primary-foreground',
        secondary: 'border-transparent bg-secondary text-secondary-foreground',
        outline: 'border-border bg-background text-foreground',
        neutral: 'border-border bg-muted text-muted-foreground',
        danger: dangerClasses,
        warning: warningClasses,
        success: successClasses,
        info: infoClasses,
        /* Legacy (pre-U1) aliases — same styles as the contract variants above. */
        alert: dangerClasses,
        decide: warningClasses,
        ok: successClasses,
      },
    },
    defaultVariants: { variant: 'neutral' },
  },
);

export type BadgeVariant = NonNullable<VariantProps<typeof badgeVariants>['variant']>;

export interface BadgeProps
  extends HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {}

export function Badge({ className, variant, ...props }: BadgeProps) {
  return <span className={cn(badgeVariants({ variant }), className)} {...props} />;
}
